import { FieldType, KeyValuePair, ViewModelBase, isEmptyOrWhitespace } from "@shoothill/core";
import { action, computed, observable, runInAction } from "mobx";

import { AppUrls } from "AppUrls";
import { PlanOfWorkStage } from "Globals/Models/Enums/PlanOfWorkStage";
import { ServerViewModel } from "Globals/ViewModels/ServerViewModel";
import { AddPlanOfWorkModel } from ".";
import { PlansOfWorkItemViewModel } from "../Table";

export class AddPlanOfWorkViewModel extends ViewModelBase<AddPlanOfWorkModel> {
    public server: ServerViewModel = new ServerViewModel();

    private projectId: string;
    private parentCloseAction: (refreshPage: boolean) => void;

    constructor(projectId: string, closeAction: (refreshPage: boolean) => void) {
        super(new AddPlanOfWorkModel(), false);

        this.projectId = projectId;
        this.parentCloseAction = closeAction;

        this.setDecorators(AddPlanOfWorkModel);

        // Load plans of work stages.
        this.apiLoadRelatedAsync();
    }

    @action
    public setMode = (val: "create" | "copy") => {
        this.model.mode = val;
        this.selectedDrawings = [];
        this.availableDrawings = [];
        this.model.planToCopyFrom = null;
        this.model.planOfWorkStage = null;
    };

    @observable public availableDrawings: IPlanOfWorkDrawing[] = [];
    @observable public selectedDrawings: IPlanOfWorkDrawing[] = [];

    @computed
    public get outputs() {
        const map = this.availableDrawings.reduce((acc, d) => {
            if (!acc.has(d.outputId)) {
                acc.set(d.outputId, { id: d.outputId, name: d.outputName });
            }
            return acc;
        }, new Map<string, { id: string; name: string }>());

        return Array.from(map.values());
    }

    // #region Plan of Work Stages

    @action
    public setPlanToCopyFrom = (plan: PlansOfWorkItemViewModel) => {
        this.model.planToCopyFrom = plan;
        this.loadDrawingsForPlan(plan.id);
    };

    @computed
    public get planOfWorkStages() {
        return this.model.planOfWorkStages;
    }

    @computed
    public get planOfWorkStage() {
        return this.model.planOfWorkStage;
    }

    @action
    public setPlanOfWorkStage = (value: PlanOfWorkStage | null) => {
        this.model.planOfWorkStage = value ?? AddPlanOfWorkModel.DEFAULT_PLANOFWORKSTAGE;
        this.isFieldValid("planOfWorkStage");
    };

    // #endregion Plan of Work Stages

    // #region Actions

    @action
    public cancel = () => this.parentCloseAction(false);

    @action
    public toggleDrawingSelection = (drawing: IPlanOfWorkDrawing): void => {
        const exists = this.selectedDrawings.find((d) => d.drawingId === drawing.drawingId);
        if (exists) {
            this.selectedDrawings = this.selectedDrawings.filter((d) => d.drawingId !== drawing.drawingId);
        } else {
            this.selectedDrawings.push(drawing);
        }
    };

    public isDrawingSelected = (drawing: IPlanOfWorkDrawing): boolean => {
        return this.selectedDrawings.some((d) => d.drawingId === drawing.drawingId);
    };

    // #endregion Actions

    // #region Api Actions

    @action
    private loadDrawingsForPlan = async (planOfWorkId: string): Promise<void> => {
        this.availableDrawings = []; // Reset
        this.selectedDrawings = [];

        await this.server.query<any>(
            () => this.Get(AppUrls.Server.Projects.ProjectTrackers.OutputTracker.PlansOfWork.GetDrawings.replace("{planOfWorkId}", planOfWorkId)),
            (result) => {
                runInAction(() => {
                    this.availableDrawings = result ?? [];
                    this.selectedDrawings = [...this.availableDrawings]; // all selected by default
                });
            },
            "Failed to load drawings",
        );
    };

    @action
    public onSave = async () => {
        if (this.model.mode === "create") {
            await this.apiInsertPlanOfWorkAsync();
        } else {
            await this.copyPlanOfWorkAsync();
        }
    };

    @action
    private copyPlanOfWorkAsync = async (): Promise<void> => {
        if (!this.model.planToCopyFrom) {
            this.setSnackMessage("Please select an existing stage to copy.");
            this.setSnackType(this.SNACKERROR);
            this.setSnackbarState(true);
            return;
        }

        const payload = {
            projectId: this.projectId,
            PlanOfWorkStageId: this.model.planOfWorkStage?.id,
            planOfWorkId: this.model.planToCopyFrom.id,
            selectedDrawingIds: this.selectedDrawings.map((d) => d.drawingId),
        };

        await this.server.command<any>(
            () => this.Post(AppUrls.Server.Projects.ProjectTrackers.OutputTracker.PlansOfWork.CreateCopy, payload),
            (result) => this.parentCloseAction(true),
            this.isModelValid,
            "Error copying plan of work",
        );

        if (this.server.HaveValidationMessage) {
            this.setSnackMessage(this.server.ValidationMessage);
            this.setSnackType(this.SNACKERROR);
            this.setSnackbarState(true);
        }
    };

    @action
    public apiInsertPlanOfWorkAsync = async (): Promise<void> => {
        await this.server.command<any>(
            () => this.Post(AppUrls.Server.Projects.ProjectTrackers.OutputTracker.PlansOfWork.Insert.replace("{projectid}", this.projectId), this.model.toDto()),
            (result) => this.parentCloseAction(true),
            this.isModelValid,
            "Error whilst saving the new stage",
        );

        if (this.server.HaveValidationMessage) {
            this.setSnackMessage(this.server.ValidationMessage);
            this.setSnackType(this.SNACKERROR);
            this.setSnackbarState(true);
        }
    };

    @action
    public apiLoadRelatedAsync = async (): Promise<void> => {
        await this.server.query<any>(
            () => this.Get(AppUrls.Server.Projects.ProjectTrackers.OutputTracker.PlansOfWork.Related.replace("{projectid}", this.projectId)),
            (result) => {
                this.model.fromDto(result);
            },
            "Error whilst loading the stage data",
        );

        if (this.server.HaveValidationMessage) {
            this.setSnackMessage(this.server.ValidationMessage);
            this.setSnackType(this.SNACKERROR);
            this.setSnackbarState(true);
        }
    };

    // #endregion Api Actions

    // #region Snack Bar

    public SNACKERROR = "error";

    @observable
    public snackbarState = false;

    @observable
    public snackType = "";

    @action
    public setSnackbarState = (val: boolean) => {
        this.snackbarState = val;
    };

    @observable
    public snackMessage = "";

    @action
    public setSnackMessage = (val: string) => {
        this.snackMessage = val;
    };

    @action
    public setSnackType = (val: string) => {
        this.snackType = val;
    };

    // #endregion Snack Bar

    // #region Boilerplate

    public async isFieldValid(fieldName: keyof FieldType<AddPlanOfWorkModel>): Promise<boolean> {
        const { isValid, errorMessage } = await this.validateDecorators(fieldName);

        this.setError(fieldName, errorMessage);
        this.setValid(fieldName, isValid);

        return isValid;
    }

    public afterUpdate: undefined;
    public beforeUpdate: undefined;

    // #endregion Boliderplate
}

export interface IPlanOfWorkDrawing {
    outputId: string;
    outputName: string;
    drawingId: string;
    drawingFileName: string;
    drawingAttachmentUrl: string;
    drawingInlineUrl: string;
    autodeskVersionId: string | null;
}
